import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BottomButtonsWrapper } from 'src/components/JobSpellCheckPopup/BottomButtonsWrapper';
import {
  getActiveResultQuestionText,
  getActiveResultTextValue,
  getChecklistNonCompliancesSummaryByType,
  getChecklistSpellCheckArray,
  getChecklistWordsArray,
  generateCombinedArray,
  getDatasheetSpellCheckArray,
  getDatasheetWordsArray,
  getNewChecklistStoredResult,
  getNewTextValueFromResult,
  getWordsArrayWithoutIgnored,
  updateResultsWithNewResult,
} from 'src/components/JobSpellCheckPopup/utils';
import { HeaderButtonsWrapper } from 'src/components/JobSpellCheckPopup/HeaderButtonsWrapper';
import { JobSpellCheckPopupProptypes } from 'src/components/JobSpellCheckPopup/propTypes';
import { Popup } from 'src/components/Popup';
import Loader from 'src/components/Loader';
import { RESULT_TYPE } from 'src/components/JobSpellCheckPopup/types';
import { useJobCreateSpellIgnore } from 'src/hooks/spellCheck/useJobCreateSpellIgnore';
import { useJobSpellCheck } from 'src/hooks/spellCheck/useJobSpellCheck';
import { useJobSpellIgnores } from 'src/hooks/spellCheck/useJobSpellIgnores';
import s from 'src/components/JobSpellCheckPopup/JobSpellCheckPopup.styles';

/* clean spellcheck array = array with results for spellcheck popup. That's the array we manipulate in this component. */

export const JobSpellCheckPopup = props => {
  const {
    datasheets,
    isSavingJobResultsLoading,
    jobId,
    onClose,
    /* to save results globally, that's a graphql mutation request */
    onSaveChecklist,
    onSaveDatasheet,
    /* to change local state results in JobForm */
    setResults,
    /* Those are results inherited from checklist/datasheet form */
    storedChecklistsResults,
    storedDatasheetsResults,
  } = props;

  const { t } = useTranslation();

  /* That's an array we use everywhere to manipulate data */
  const [combinedResultsWithSpellCheck, setCombinedResultsWithSpellCheck] = useState([]);
  // We assign -1 to indicate it's uninitialized - instead of undefined, so it's always a number type
  const [activeCombinedResultIndex, setActiveCombinedResultIndex] = useState(-1);
  /* all the ignored words stored in array */
  const [ignoredWords, setIgnoredWords] = useState([]);

  /* Transform stored checklist & results into clean array */
  const checklistSpellCheckArray = getChecklistSpellCheckArray(storedChecklistsResults);
  /* Transform stored datasheet & results into clean array */
  const datasheetSpellCheckArray = getDatasheetSpellCheckArray(storedDatasheetsResults);

  /* Extract all the words from datasheets and checklists and then send a request to backend to obtain an array with typos  */
  const {
    data: jobDatasheetSpellCheckResponse,
    loading: jobDatasheetSpellCheckLoading
  } = useJobSpellCheck({ texts: getDatasheetWordsArray(datasheetSpellCheckArray) });
  const {
    data: jobChecklistSpellCheckResponse,
    loading: jobChecklistSpellCheckLoading
  } = useJobSpellCheck({ texts: getChecklistWordsArray(checklistSpellCheckArray) });
  /* Send a request to backend to get an array of ignored words  */
  const {
    data: jobSpellIgnores,
    loading: jobSpellIgnoresLoading
  } = useJobSpellIgnores({ jobId });

  const initialLoading =
    jobDatasheetSpellCheckLoading ||
    jobChecklistSpellCheckLoading ||
    jobSpellIgnoresLoading;

  const { createSpellIgnore, isLoading: isLoadingCreateSpellIgnore } = useJobCreateSpellIgnore();

  /* Every time we get a response with typos or ignored words from backend we rebuild clean spellcheck results array and save ignored words */
  useEffect(() => {
    if (jobDatasheetSpellCheckResponse && jobChecklistSpellCheckResponse && jobSpellIgnores) {

      setIgnoredWords(jobSpellIgnores.jobSpellIgnores.map(ignoredObject => ignoredObject.word));

      const datasheetSpellCheckWithoutIgnores = getWordsArrayWithoutIgnored(
        jobDatasheetSpellCheckResponse.jobSpellCheck,
        jobSpellIgnores.jobSpellIgnores
      );

      const checklistSpellCheckWithoutIgnores = getWordsArrayWithoutIgnored(
        jobChecklistSpellCheckResponse.jobSpellCheck,
        jobSpellIgnores.jobSpellIgnores
      );

      const checklistResultsArray = generateCombinedArray(
        checklistSpellCheckArray,
        checklistSpellCheckWithoutIgnores,
        RESULT_TYPE.CHECKLIST,
      );

      const datasheetResultsArray = generateCombinedArray(
        datasheetSpellCheckArray,
        datasheetSpellCheckWithoutIgnores,
        RESULT_TYPE.DATASHEET,
      );

      setCombinedResultsWithSpellCheck(datasheetResultsArray.concat(checklistResultsArray));
      setActiveCombinedResultIndex(0);
    }
  }, [jobDatasheetSpellCheckResponse, jobChecklistSpellCheckResponse, jobSpellIgnores]);

  /* DROP RESULT FROM ARRAY */
  const dropResult = () => {
    const newResults = [...combinedResultsWithSpellCheck];

    newResults.splice(activeCombinedResultIndex, 1);

    if (activeCombinedResultIndex === combinedResultsWithSpellCheck.length - 1) {
      setActiveCombinedResultIndex(activeCombinedResultIndex - 1);
    }

    setCombinedResultsWithSpellCheck(newResults);
  };

  const dropSelectedWordFromAllResults = (word) => {
    let newActiveResultIndex = activeCombinedResultIndex;

    if (activeCombinedResultIndex === combinedResultsWithSpellCheck.length - 1) {
      let occurrencesTotal = 0;

      combinedResultsWithSpellCheck.forEach((value) => {
        if (value.spellCheck.word === word) {
          occurrencesTotal += 1;
        }
      });

      newActiveResultIndex -= occurrencesTotal;
    } else {
      let occurrencesBefore = 0;

      combinedResultsWithSpellCheck.forEach((value, index) => {
        if (value.spellCheck.word === word && index < activeCombinedResultIndex) {
          occurrencesBefore += 1;
        }
      });

      newActiveResultIndex -= occurrencesBefore;
    }

    setCombinedResultsWithSpellCheck(
      combinedResultsWithSpellCheck.filter(value => value.spellCheck.word !== word)
    );
    setActiveCombinedResultIndex(newActiveResultIndex);
  };

  /* IGNORE SINGLE WORD */
  const onIgnore = async () => {
    const selectedWord = combinedResultsWithSpellCheck[activeCombinedResultIndex].spellCheck.word;

    const isAlreadyIgnored = ignoredWords
      .some(ignoredObject => ignoredObject === selectedWord);

    if (!isAlreadyIgnored) {
      await createSpellIgnore({ jobId, words: [selectedWord] });
      setIgnoredWords([...ignoredWords, selectedWord]);
    }

    dropResult();
  };

  /* IGNORE ALL OCCURRENCES OF CURRENTLY SELECTED WORD */
  const onIgnoreAll = async () => {
    const selectedWord = combinedResultsWithSpellCheck[activeCombinedResultIndex].spellCheck.word;

    const isAlreadyIgnored = ignoredWords
      .some(ignoredObject => ignoredObject === selectedWord);

    if (!isAlreadyIgnored) {
      await createSpellIgnore({ jobId, words: [selectedWord] });
      setIgnoredWords([...ignoredWords, selectedWord]);
    }

    dropSelectedWordFromAllResults(selectedWord);
  };

  /* REPLACE SINGLE WORD */
  const onReplace = () => {
    const selectedResult = { ...combinedResultsWithSpellCheck[activeCombinedResultIndex] };
    const isEmpty = !selectedResult.spellCheck.replaceValue.trim();

    if (isEmpty) {
      return;
    }

    if (selectedResult.type === RESULT_TYPE.DATASHEET) {
      const storedResult = { ...storedDatasheetsResults.find(element => element.id === selectedResult.data.resultId) };

      if(selectedResult.result.type === 'result') {
        storedResult.result.value = getNewTextValueFromResult(selectedResult);

        const newStoredResults = updateResultsWithNewResult(storedDatasheetsResults, storedResult);

        setResults(newStoredResults);
        onSaveDatasheet(selectedResult.data.datasheetId, selectedResult.data.datasheetVersion, storedResult, dropResult);
      }

      if (selectedResult.result.type === 'comments') {
        storedResult.comments = getNewTextValueFromResult(selectedResult);
        
        const newStoredResults = updateResultsWithNewResult(storedDatasheetsResults, storedResult);

        setResults(newStoredResults);
        onSaveDatasheet(selectedResult.data.datasheetId, selectedResult.data.datasheetVersion, storedResult, dropResult);
      }

    } else if (selectedResult.type === RESULT_TYPE.CHECKLIST) {
      const storedResult = { ...storedChecklistsResults.find(element => element.id === selectedResult.data.resultId) };

      if (selectedResult.result.type === 'comments' || selectedResult.result.type === 'correctiveActionTaken') {
        storedResult[selectedResult.result.type] = getNewTextValueFromResult(selectedResult);

        const newStoredResults = updateResultsWithNewResult(storedChecklistsResults, storedResult);

        setResults(newStoredResults);
        onSaveChecklist(selectedResult.data.checklistId, selectedResult.data.checklistVersion, selectedResult.data.checklistVersion, storedResult, dropResult);
      }

      if (selectedResult.result.isNonCompliance) {
        const newStoredResult = getNewChecklistStoredResult(selectedResult, storedResult);
        const newStoredResults = updateResultsWithNewResult(storedChecklistsResults, newStoredResult);

        setResults(newStoredResults);
        onSaveChecklist(selectedResult.data.checklistId, selectedResult.data.checklistVersion, newStoredResult, dropResult);
      }
    }
  };

  /* REPLACE ALL OCCURRENCES OF CURRENTLY SELECTED WORD */
  const onReplaceAll = () => {
    const newArray = [...combinedResultsWithSpellCheck];
    const uniqueResults = {
      datasheetResults: [],
      checklistComments: [],
      checklistCorrectiveAction: [],
      checklistNonCompliances: [],
      uniqueChecklistsResults: [],
    };
    // typo word
    const selectedWord = newArray[activeCombinedResultIndex].spellCheck.word;
    // replace value
    const replaceToWord = newArray[activeCombinedResultIndex].spellCheck.replaceValue;
    const isEmpty = !newArray[activeCombinedResultIndex].spellCheck.replaceValue.trim();

    if (isEmpty) {
      return;
    }

    // gets only results which are from datasheet
    const datasheetResultsArray = newArray
      .filter(item => item.type === RESULT_TYPE.DATASHEET &&
        item.spellCheck.word === selectedWord
      );
    // gets only results which are from checklist -> comments
    const checklistCommentResultsArray = newArray
      .filter(item => item.type === RESULT_TYPE.CHECKLIST &&
        item.result.type === 'comments' &&
        item.spellCheck.word === selectedWord
      );
    // gets only results which are from checklist -> correctiveActionTaken
    const checklistCorrectiveActionResultsArray = newArray
      .filter(item => item.type === RESULT_TYPE.CHECKLIST &&
        item.result.type === 'correctiveActionTaken' &&
        item.spellCheck.word === selectedWord
      );
    // gets only results which are from checklist -> nonCompliances
    const checklistNonCompliancesArray = newArray
      .filter(item => item.type === RESULT_TYPE.CHECKLIST &&
        item.result.isNonCompliance &&
        item.spellCheck.word === selectedWord
      );

    const uniqueResultsReadyToUpdate = [];

    // put each replaced word to unique -> datasheet results array
    datasheetResultsArray.forEach(item => {
      const resultAlreadyInArray = uniqueResults.datasheetResults
        .find(element => element.data.resultId === item.data.resultId);
      const newResult = item;

      if (resultAlreadyInArray) {
        newResult.result.value = resultAlreadyInArray.result.value;
      }

      newResult.result.value = getNewTextValueFromResult(newResult, replaceToWord);

      if (resultAlreadyInArray) {
        const index = uniqueResults.datasheetResults
          .findIndex((element) => element.data.resultId === item.data.resultId);
        uniqueResults.datasheetResults[index] = newResult;
      } else {
        uniqueResults.datasheetResults.push(newResult);
      }
    });

    // put each replaced word to unique -> checklist comments results array
    checklistCommentResultsArray.forEach(item => {
      const resultAlreadyInArray = uniqueResults.checklistComments
        .find(element => element.data.resultId === item.data.resultId);
      const newResult = item;

      if (resultAlreadyInArray) {
        newResult.result.value = resultAlreadyInArray.result.value;
      }

      if (!uniqueResults.uniqueChecklistsResults.some(id => id === item.data.resultId)) {
        uniqueResults.uniqueChecklistsResults.push(item.data.resultId);
      }

      newResult.result.value = getNewTextValueFromResult(newResult, replaceToWord);

      if (resultAlreadyInArray) {
        const index = uniqueResults.checklistComments
          .findIndex((element) => element.data.resultId === item.data.resultId);
        uniqueResults.checklistComments[index] = newResult;
      } else {
        uniqueResults.checklistComments.push(newResult);
      }
    });

    // put each replaced word to unique -> checklist correctiveAction results array
    checklistCorrectiveActionResultsArray.forEach(item => {
      const resultAlreadyInArray = uniqueResults.checklistCorrectiveAction
        .find(element => element.data.resultId === item.data.resultId);
      const newResult = item;

      if (resultAlreadyInArray) {
        newResult.result.value = resultAlreadyInArray.result.value;
      }

      if (!uniqueResults.uniqueChecklistsResults.some(id => id === item.data.resultId)) {
        uniqueResults.uniqueChecklistsResults.push(item.data.resultId);
      }

      newResult.result.value = getNewTextValueFromResult(newResult, replaceToWord);

      if (resultAlreadyInArray) {
        const index = uniqueResults.checklistCorrectiveAction
          .findIndex((element) => element.data.resultId === item.data.resultId);
        uniqueResults.checklistCorrectiveAction[index] = newResult;
      } else {
        uniqueResults.checklistCorrectiveAction.push(newResult);
      }
    });

    // put each replaced word to unique -> checklist nonCompliances results array
    checklistNonCompliancesArray.forEach(item => {
      const resultAlreadyInArray = uniqueResults.checklistNonCompliances
        .find(element => element.result.findingId === item.result.findingId && element.result.type === item.result.type);
      const newResult = item;

      if (resultAlreadyInArray) {
        newResult.result.value = resultAlreadyInArray.result.value;
      }

      if (!uniqueResults.uniqueChecklistsResults.some(id => id === item.data.resultId)) {
        uniqueResults.uniqueChecklistsResults.push(item.data.resultId);
      }

      newResult.result.value = getNewTextValueFromResult(newResult, replaceToWord);

      if (resultAlreadyInArray) {
        const index = uniqueResults.checklistNonCompliances
          .findIndex(
            (element) => element.result.findingId === item.result.findingId && element.result.type === item.result.type
          );
        uniqueResults.checklistNonCompliances[index] = newResult;
      } else {
        uniqueResults.checklistNonCompliances.push(newResult);
      }
    });

    uniqueResults.datasheetResults.forEach(item => {
      const storedResult = storedDatasheetsResults.find(element => element.id === item.data.resultId);

      if(item.result.type === 'result') {
        storedResult.result.value = item.result.value;
      }

      if(item.result.type === 'comments') {
        storedResult.comments = item.result.value;
      }

      uniqueResultsReadyToUpdate.push({
        data: storedResult,
        type: RESULT_TYPE.DATASHEET,
      });
    });

    uniqueResults.uniqueChecklistsResults.forEach(resultId => {
      const storedResult = storedChecklistsResults.find(element => element.id === resultId);

      const resultWithChangedComment = uniqueResults.checklistComments
        .find(element => element.data.resultId === resultId);

      const resultWithChangedCorrectiveAction = uniqueResults.checklistCorrectiveAction
        .find(element => element.data.resultId === resultId);

      if (resultWithChangedComment) {
        storedResult.comments = resultWithChangedComment.result.value;
      }

      if(resultWithChangedCorrectiveAction) {
        storedResult.correctiveActionTaken = resultWithChangedCorrectiveAction.result.value;
      }

      uniqueResults.checklistNonCompliances
        .filter(element => element.data.resultId === resultId)
        .forEach(nonCompliance => {
          let storedNonComplianceIndex = storedResult.nonCompliances
            .findIndex(element => element.id === nonCompliance.result.findingId);

          if(storedNonComplianceIndex === -1) {
            storedNonComplianceIndex = storedResult.nonCompliances.findIndex(element => element[nonCompliance.result.type] === selectedWord);
          }

          if (storedNonComplianceIndex !== -1 && storedResult.nonCompliances[storedNonComplianceIndex]) {
            storedResult.nonCompliances[storedNonComplianceIndex][nonCompliance.result.type] = nonCompliance.result.value;
          }
        });

      const hasLegalCompliances = storedResult.nonCompliances.some(nonCompliance => nonCompliance.legalRequirements);
      const hasDetailedFinding = storedResult.nonCompliances.some(nonCompliance => nonCompliance.detailedFinding);
      const hasRecommendations = storedResult.nonCompliances.some(nonCompliance => nonCompliance.recommendations);

      if (hasLegalCompliances) {
        storedResult.legalRequirements = getChecklistNonCompliancesSummaryByType(storedResult, 'legalRequirements');
      }

      if (hasDetailedFinding) {
        storedResult.detailedFinding = getChecklistNonCompliancesSummaryByType(storedResult, 'detailedFinding');
      }

      if (hasRecommendations) {
        storedResult.recommendations = getChecklistNonCompliancesSummaryByType(storedResult, 'recommendations');
      }

      uniqueResultsReadyToUpdate.push({
        data: storedResult,
        type: RESULT_TYPE.CHECKLIST,
      });
    });

    uniqueResultsReadyToUpdate.forEach(result => {
      if (result.type === RESULT_TYPE.DATASHEET) {
        const newStoredResults = updateResultsWithNewResult(storedDatasheetsResults, result.data);

        setResults(newStoredResults);
        onSaveDatasheet(result.data.datasheetId, result.data.datasheetVersion, result.data, () => dropSelectedWordFromAllResults(selectedWord));
      } else {
        const newStoredResults = updateResultsWithNewResult(storedChecklistsResults, result.data);

        setResults(newStoredResults);
        onSaveChecklist(result.data.checklistId, result.data.checklistVersion, result.data, () => dropSelectedWordFromAllResults(selectedWord));
      }
    });
  };

  const onChangeValue = e => {
    const newValue = e.target.value;
    const newCombinedResults = [...combinedResultsWithSpellCheck];

    newCombinedResults[activeCombinedResultIndex].spellCheck.replaceValue = newValue;
    setCombinedResultsWithSpellCheck(newCombinedResults);
  };

  const onSelectNext = () => {
    setActiveCombinedResultIndex(activeCombinedResultIndex + 1);
  };

  const onSelectPrevious = () => {
    setActiveCombinedResultIndex(activeCombinedResultIndex - 1);
  };

  const activeResult = activeCombinedResultIndex >= 0 && combinedResultsWithSpellCheck.length &&
    combinedResultsWithSpellCheck[activeCombinedResultIndex];
  const resultQuestion = getActiveResultQuestionText(activeResult, t, datasheets);

  const replaceButtonsDisabled = isSavingJobResultsLoading || isLoadingCreateSpellIgnore || !activeResult.spellCheck?.replaceValue.trim();
  const ignoreButtonsDisabled = isSavingJobResultsLoading || isLoadingCreateSpellIgnore;

  return (
    <Popup
      onClose={onClose}
      title={<s.Title>{t('SPELL_CHECK')}</s.Title>}
      >
      {Boolean(initialLoading) && <Loader text="PLEASE_WAIT" withText={true} />}
      {Boolean(activeResult && resultQuestion && !initialLoading) && (
        <s.SpellCheckPopupWrapper>
          <s.HeaderBlock>
            <s.HeaderText>
              {t('SPELLING_ERROR_OF', {
                index: activeCombinedResultIndex + 1,
                length: combinedResultsWithSpellCheck.length,
              })}
            </s.HeaderText>

            <HeaderButtonsWrapper
              disabled={isSavingJobResultsLoading || isLoadingCreateSpellIgnore}
              disabledNext={activeCombinedResultIndex >= combinedResultsWithSpellCheck.length - 1}
              disabledPrevious={activeCombinedResultIndex === 0}
              onSelectNext={onSelectNext}
              onSelectPrevious={onSelectPrevious}
            />
          </s.HeaderBlock>

          <s.UnknownWordHeading>
            {`${t('UNKNOWN_WORD')}: `}
            <mark>{activeResult.spellCheck.word}</mark>
          </s.UnknownWordHeading>

          <s.FieldBox>
            <s.FieldBoxHeaderWrapper withBorder>
              {resultQuestion}
            </s.FieldBoxHeaderWrapper>
            <s.FieldBoxContentWrapper>
              {getActiveResultTextValue(activeResult)}
            </s.FieldBoxContentWrapper>
          </s.FieldBox>

          <s.FieldBox>
            <s.FieldBoxHeaderWrapper>
              {`${t('REPLACE_WITH', { word: activeResult.spellCheck.word })}:`}
            </s.FieldBoxHeaderWrapper>
            <s.FieldBoxContentWrapper fullBorder>
              <s.InvisibleInput
                onChange={onChangeValue}
                type="text"
                value={activeResult.spellCheck.replaceValue}
              />
            </s.FieldBoxContentWrapper>
          </s.FieldBox>

          <BottomButtonsWrapper
            ignoreButtonsDisabled={ignoreButtonsDisabled}
            onIgnore={onIgnore}
            onIgnoreAll={onIgnoreAll}
            onReplace={onReplace}
            onReplaceAll={onReplaceAll}
            replaceButtonsDisabled={replaceButtonsDisabled}
          />
        </s.SpellCheckPopupWrapper>
      )}
      {Boolean(!activeResult &&  !initialLoading) && (
        <s.NoTyposWrapper>
          <s.Icon name="check" />
          <s.NoTyposText>{t('SPELL_CHECK_SUCCESS')}</s.NoTyposText>
        </s.NoTyposWrapper>
      )}
    </Popup>
  );
};

JobSpellCheckPopup.propTypes = JobSpellCheckPopupProptypes;
